import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Collapse,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ChevronDown } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge as ShadcnBadge } from "@/components/ui/badge";
import { Button as ShadcnButton } from "@/components/ui/button";
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import type { PineParseIssue } from "../../types/reportStrategyPine";
import type { StrategyPineBadgeVariant } from "../../views/Dashboard/Strategies/strategyTablePineBadges";
import {
  enrichParseIssueForDisplay,
  formatIssueBlocks,
  groupParseIssuesByKind,
  issueSeverityAlertStatus,
} from "../../utils/pineIrUi";
import {
  STRATEGY_PINE_SECTION_DESC_CLASS,
  STRATEGY_PINE_SECTION_LABEL_CLASS,
} from "./StrategyPineSectionCard";
import {
  STRATEGY_PINE_ALERT_CLASS,
  StrategyPineAlertIcon,
} from "./strategyPineAlertUi";

export type StrategyPineParseIssuesProps = {
  issues: PineParseIssue[];
  subtleText: string;
  textColor: string;
  borderColor: string;
  onGoToLine?: (line: number) => void;
  /** When true, show Blocks merge / Review / Informational sections. */
  grouped?: boolean;
  /** Use shadcn cards and typography (create-strategy dialog). */
  brandFields?: boolean;
};

function issueBadgeVariant(issue: PineParseIssue): StrategyPineBadgeVariant {
  const display = enrichParseIssueForDisplay(issue);
  if (display.blocks?.includes("merge") || display.severity === "error") {
    return "pine-parse-failed";
  }
  if (display.severity === "info") {
    return "pine-meta";
  }
  return "pine-parse-partial";
}

function IssueRowBrand({
  issue,
  onGoToLine,
}: {
  issue: PineParseIssue;
  onGoToLine?: (line: number) => void;
}): React.ReactElement {
  const [fixOpen, setFixOpen] = useState(false);
  const display = enrichParseIssueForDisplay(issue);
  const line = display.line;
  const blocksLabel = display.blocks?.length ? formatIssueBlocks(display.blocks) : "Advisory";

  return (
    <Card
      size="sm"
      data-strategy-pine-parse-issue=""
      className="gap-0 rounded-lg py-0 shadow-none ring-1 ring-input"
    >
      <CardHeader className="gap-1 px-3 pt-3 pb-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-1">
            <CardTitle className={cn("text-sm font-normal", STRATEGY_PINE_SECTION_LABEL_CLASS)}>
              {display.title ?? display.code}
            </CardTitle>
            <CardDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
              {display.message}
            </CardDescription>
          </div>
          <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
            <ShadcnBadge variant="outline" className="font-mono text-[0.65rem] uppercase">
              {display.code}
            </ShadcnBadge>
            <ShadcnBadge variant={issueBadgeVariant(issue)}>{blocksLabel}</ShadcnBadge>
          </div>
        </div>
      </CardHeader>

      {(typeof line === "number" && onGoToLine) || display.expectation ? (
        <CardFooter className="flex flex-wrap items-center gap-2 border-0 bg-transparent px-3 pt-0 pb-3">
          {typeof line === "number" && onGoToLine ? (
            <ShadcnButton
              type="button"
              variant="outline"
              size="xs"
              data-font="primary"
              onClick={() => onGoToLine(line)}
            >
              Go to line {line}
              {display.line_end && display.line_end !== line ? `–${display.line_end}` : ""}
            </ShadcnButton>
          ) : null}

          {display.expectation ? (
            <Collapsible open={fixOpen} onOpenChange={setFixOpen}>
              <CollapsibleTrigger asChild>
                <ShadcnButton
                  type="button"
                  variant="ghost"
                  size="xs"
                  data-font="primary"
                  className="gap-1"
                >
                  {fixOpen ? "Hide fix" : "How to fix"}
                  <ChevronDown
                    className={cn("size-3.5 transition-transform", fixOpen && "rotate-180")}
                    aria-hidden
                  />
                </ShadcnButton>
              </CollapsibleTrigger>
              <CollapsibleContent className="w-full basis-full">
                <pre
                  className="mt-2 w-full overflow-x-auto rounded-lg border border-input bg-muted/30 px-2.5 py-2 font-mono text-xs leading-relaxed whitespace-pre-wrap text-foreground"
                >
                  {display.expectation}
                </pre>
              </CollapsibleContent>
            </Collapsible>
          ) : null}
        </CardFooter>
      ) : null}
    </Card>
  );
}

function IssueSectionBrand({
  title,
  issues,
  onGoToLine,
}: {
  title: string;
  issues: PineParseIssue[];
  onGoToLine?: (line: number) => void;
}): React.ReactElement | null {
  if (!issues.length) return null;
  return (
    <div className="space-y-2">
      <p className={STRATEGY_PINE_SECTION_LABEL_CLASS}>{title}</p>
      <div className="flex flex-col gap-2">
        {issues.map((issue, idx) => (
          <IssueRowBrand
            key={`${issue.code}-${issue.line ?? idx}-${idx}`}
            issue={issue}
            onGoToLine={onGoToLine}
          />
        ))}
      </div>
    </div>
  );
}

function IssueRow({
  issue,
  subtleText,
  textColor,
  borderColor,
  onGoToLine,
}: {
  issue: PineParseIssue;
  subtleText: string;
  textColor: string;
  borderColor: string;
  onGoToLine?: (line: number) => void;
}): React.ReactElement {
  const [showFix, setShowFix] = useState(false);
  const display = enrichParseIssueForDisplay(issue);
  const line = display.line;
  const status = issueSeverityAlertStatus(display.severity);

  return (
    <Box
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="8px"
      p={3}
      bg={status === "error" ? "red.50" : status === "info" ? "blue.50" : "orange.50"}
      _dark={{
        bg: "whiteAlpha.100",
      }}
    >
      <HStack justify="space-between" align="flex-start" mb={1} wrap="wrap" gap={2}>
        <Box flex="1" minW="200px">
          <Text fontSize="sm" fontWeight="semibold" color={textColor}>
            {display.title ?? display.code}
          </Text>
          <Text fontSize="sm" color={subtleText} mt={1}>
            {display.message}
          </Text>
        </Box>
        <HStack spacing={1} flexWrap="wrap">
          <Badge variant="outline" fontSize="xs">
            {display.code}
          </Badge>
          {display.blocks?.length ? (
            <Badge
              colorScheme={display.blocks.includes("merge") ? "red" : "gray"}
              variant="subtle"
              fontSize="xs"
            >
              {formatIssueBlocks(display.blocks)}
            </Badge>
          ) : (
            <Badge variant="subtle" fontSize="xs">
              Advisory
            </Badge>
          )}
        </HStack>
      </HStack>

      <HStack spacing={2} mt={2} flexWrap="wrap">
        {typeof line === "number" && onGoToLine ? (
          <Button size="xs" variant="outline" onClick={() => onGoToLine(line)}>
            Go to line {line}
            {display.line_end && display.line_end !== line ? `–${display.line_end}` : ""}
          </Button>
        ) : null}
        {display.expectation ? (
          <Button size="xs" variant="ghost" onClick={() => setShowFix((v) => !v)}>
            {showFix ? "Hide fix" : "How to fix"}
          </Button>
        ) : null}
      </HStack>

      {display.expectation ? (
        <Collapse in={showFix} animateOpacity>
          <Box
            mt={2}
            p={2}
            borderRadius="6px"
            borderWidth="1px"
            borderColor={borderColor}
            bg="blackAlpha.50"
          >
            <Text fontSize="xs" fontFamily="mono" whiteSpace="pre-wrap" color={textColor}>
              {display.expectation}
            </Text>
          </Box>
        </Collapse>
      ) : null}
    </Box>
  );
}

function IssueSection({
  title,
  issues,
  subtleText,
  textColor,
  borderColor,
  onGoToLine,
}: {
  title: string;
  issues: PineParseIssue[];
  subtleText: string;
  textColor: string;
  borderColor: string;
  onGoToLine?: (line: number) => void;
}): React.ReactElement | null {
  if (!issues.length) return null;
  return (
    <Box mb={3}>
      <Text fontWeight="medium" color={textColor} fontSize="sm" mb={2}>
        {title}
      </Text>
      <VStack align="stretch" spacing={2}>
        {issues.map((issue, idx) => (
          <IssueRow
            key={`${issue.code}-${issue.line ?? idx}-${idx}`}
            issue={issue}
            subtleText={subtleText}
            textColor={textColor}
            borderColor={borderColor}
            onGoToLine={onGoToLine}
          />
        ))}
      </VStack>
    </Box>
  );
}

export function StrategyPineParseIssues({
  issues,
  subtleText,
  textColor,
  borderColor,
  onGoToLine,
  grouped = false,
  brandFields = false,
}: StrategyPineParseIssuesProps): React.ReactElement | null {
  if (!issues.length) return null;

  if (brandFields) {
    const { blocksMerge, review, informational } = groupParseIssuesByKind(issues);
    return (
      <div className="mt-3 space-y-4" data-strategy-pine-parse-issues="">
        <p className={STRATEGY_PINE_SECTION_LABEL_CLASS}>What to fix</p>
        {grouped ? (
          <>
            <IssueSectionBrand
              title="Blocks merge and compose"
              issues={blocksMerge}
              onGoToLine={onGoToLine}
            />
            <IssueSectionBrand
              title="Review before merge"
              issues={review}
              onGoToLine={onGoToLine}
            />
            <IssueSectionBrand title="Informational" issues={informational} onGoToLine={onGoToLine} />
          </>
        ) : (
          <div className="flex flex-col gap-2">
            {issues.map((issue, idx) => (
              <IssueRowBrand
                key={`${issue.code}-${issue.line ?? idx}-${idx}`}
                issue={issue}
                onGoToLine={onGoToLine}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (grouped) {
    const { blocksMerge, review, informational } = groupParseIssuesByKind(issues);
    return (
      <Box>
        <Text fontWeight="semibold" color={textColor} fontSize="sm" mb={2}>
          Parse issues
        </Text>
        <IssueSection
          title="Blocks merge & compose"
          issues={blocksMerge}
          subtleText={subtleText}
          textColor={textColor}
          borderColor={borderColor}
          onGoToLine={onGoToLine}
        />
        <IssueSection
          title="Review before merge"
          issues={review}
          subtleText={subtleText}
          textColor={textColor}
          borderColor={borderColor}
          onGoToLine={onGoToLine}
        />
        <IssueSection
          title="Informational"
          issues={informational}
          subtleText={subtleText}
          textColor={textColor}
          borderColor={borderColor}
          onGoToLine={onGoToLine}
        />
      </Box>
    );
  }

  const errors = issues.filter((i) => i.severity === "error");
  const warnings = issues.filter((i) => i.severity !== "error" && i.severity !== "info");
  const infos = issues.filter((i) => i.severity === "info");
  const ordered = [...errors, ...warnings, ...infos];

  return (
    <Box>
      <Text fontWeight="semibold" color={textColor} fontSize="sm" mb={2}>
        Parse issues
      </Text>
      <VStack align="stretch" spacing={2}>
        {ordered.map((issue, idx) => (
          <IssueRow
            key={`${issue.code}-${issue.line ?? idx}-${idx}`}
            issue={issue}
            subtleText={subtleText}
            textColor={textColor}
            borderColor={borderColor}
            onGoToLine={onGoToLine}
          />
        ))}
      </VStack>
    </Box>
  );
}

export function StrategyPineParseIssuesAlert({
  issues,
}: {
  issues: PineParseIssue[];
  subtleText?: string;
}): React.ReactElement | null {
  const { blocksMerge } = groupParseIssuesByKind(issues);
  if (!blocksMerge.length) return null;
  return (
    <Alert variant="warning" className={`mb-2 ${STRATEGY_PINE_ALERT_CLASS}`}>
      <StrategyPineAlertIcon tone="warning" />
      <AlertDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
        {blocksMerge.length} issue{blocksMerge.length === 1 ? "" : "s"} may block merge or TES
        Pinescript snapshot.
      </AlertDescription>
    </Alert>
  );
}
